import React, { useEffect, useState } from 'react';
import EndUserTaskTableView from '../view/EndUserTaskTableView';
import {
  addDaysToDateObject,
  getDifferenceInDays,
  isSameDay
} from '../../../utils/dateUtils';
import useGlobalVariables from '../../../hooks/useGlobalVariables';

const INITIAL_SORT = { id: 'dueDate', value: 'asc' };

const EndUserTaskTableEngine = ({}) => {
  const { setIsLoading } = useGlobalVariables();
  const [allTasks, setAllTasks] = useState([]);
  const [canReassign, setCanReassign] = useState(false);
  const [currentlyFiltered, setCurrentlyFiltered] = useState(false);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [sortObject, setSortObject] = useState(INITIAL_SORT);
  const [counts, setCounts] = useState({ status: {}, priority: {}, date: {} });
  const [passedFilters, setPassedFilters] = useState({
    status: [],
    priority: [],
    dateFilters: { checkedDateOptions: [], fromDate: null, toDate: null }
  });
  const [refreshSeed, setRefreshSeed] = useState(1);
  const [taskBeingEdited, setTaskBeingEdited] = useState(null);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);
  const getUserInfoByIdsPromise = ids => {
    return new Promise((resolve, reject) => {
      const VINCI_SUPPORT_ID = 'f2618836-2355-496c-b245-1c1ae2258101';
      if (!ids.length) {
        resolve({});
      }
      const usersQuery = `/users/v1/eu/search?query=[[${ids
        .map(
          one =>
            `{"field": "'id'", "op": "=", "value": "${one}", "internOp": "OR"}`
        )
        .join(',')}]]`;
      fetchAPI(usersQuery).then(response => {
        const userResults = response.results || [];
        let resultsById = {};
        userResults.forEach(one => {
          resultsById[one.id] = one;
        });
        if (ids.includes(VINCI_SUPPORT_ID) && !resultsById[VINCI_SUPPORT_ID]) {
          resultsById[VINCI_SUPPORT_ID] = {
            id: VINCI_SUPPORT_ID,
            info: {
              email: 'vinci_support@vinciworks.com',
              firstName: 'Vinci',
              lastName: 'Support'
            }
          };
        }
        resolve(resultsById);
      });
    });
  };

  const loadCanReassign = () => {
    fetchAPI(`/tasks/v1/tasks/configurations`).then(res => {
      const config = res.results && res.results.length ? res.results[0] : {};
      const value = !!config?.endUserReassignTask;
      setCanReassign(value);
    });
  };

  const loadTasks = openTaskId => {
    fetchAPI(`/tasks/v1/eu/tasks`).then(tasksRes => {
      updateTaskTableFromAPIResults(
        tasksRes,
        true,
        true,
        null,
        null,
        openTaskId
      );
    });
  };

  const loadTaskCounts = (fromDate, toDate) => {
    let allPromises, onePromise, dueDateURL;
    allPromises = [];
    onePromise = fetchAPI(
      `/tasks/v1/eu/tasks?query=[[{"what":"count","field":"*", "as": "count"}],[{"groupOn":"'status'"}]]`
    ).then(statusRes => {
      return statusRes;
    });
    allPromises.push(onePromise);
    onePromise = fetchAPI(
      `/tasks/v1/eu/tasks?query=[[{"what":"count","field":"*", "as": "count"}],[{"groupOn":"'priority'"}]]`
    ).then(priorityRes => {
      return priorityRes;
    });
    allPromises.push(onePromise);
    dueDateURL = `/tasks/v1/eu/tasks/duedate`;
    if (fromDate) {
      dueDateURL += '?start=' + fromDate;
    }
    if (toDate) {
      if (fromDate) {
        dueDateURL += '&';
      } else {
        dueDateURL += '?';
      }
      dueDateURL += 'end=' + toDate;
    }
    onePromise = fetchAPI(dueDateURL).then(dueDateRes => {
      return dueDateRes;
    });
    allPromises.push(onePromise);
    Promise.all(allPromises).then(allResults => {
      const [statusCountResult, priorityCountResult, dueDateCountResult] =
        allResults;
      let count,
        key,
        newStatusCounts = {},
        newPriorityCounts = {},
        newDueDateCounts = {};
      if (statusCountResult?.additional) {
        statusCountResult.additional.forEach(one => {
          if (one.key === 'count') {
            count = one.value || 0;
          }
          if (one.key === '?column?') {
            key = one.value;
            newStatusCounts[key] = count;
          }
        });
      }
      if (priorityCountResult?.additional) {
        priorityCountResult.additional.forEach(one => {
          if (one.key === 'count') {
            count = one.value || 0;
          }
          if (one.key === '?column?') {
            key = one.value;
            newPriorityCounts[key] = count;
          }
        });
      }
      if (dueDateCountResult.additional) {
        dueDateCountResult.additional.forEach(one => {
          if (one.key === 'due_status') {
            key = one.value;
          }
          if (one.key === 'count') {
            count = one.value || 0;
            newDueDateCounts[key] = count;
          }
        });
      }
      setCounts({
        status: newStatusCounts,
        priority: newPriorityCounts,
        date: newDueDateCounts
      });
    });
  };

  const updateTaskTableFromAPIResults = (
    tasksRes,
    isAllTasks,
    updateCounts,
    fromDate,
    toDate,
    openTaskId
  ) => {
    let userIdsToLookup = [];
    const results = tasksRes?.results || [];
    if (results && results.length) {
      userIdsToLookup = [
        ...results.map(one => one.assigneeId),
        ...results.map(one => one.metadata?.createdByUserId)
      ];
      userIdsToLookup = [...new Set(userIdsToLookup)];
      getUserInfoByIdsPromise(userIdsToLookup).then(usersRes => {
        const combinedResults = [];
        results.forEach(one => {
          let oneCombinedRes = { ...one };
          const assigneeUserMatch = usersRes[one.assigneeId];
          if (assigneeUserMatch) {
            oneCombinedRes.assigneeName =
              assigneeUserMatch.info.firstName +
                ' ' +
                assigneeUserMatch.info.lastName || '';
            oneCombinedRes.assigneeEmailAddress =
              assigneeUserMatch.info.email || '';
          } else {
            oneCombinedRes.assigneeName = '';
            oneCombinedRes.assigneeEmailAddress = '';
          }
          const authorUserMatch = usersRes[one.metadata.createdByUserId];
          if (authorUserMatch) {
            oneCombinedRes.authorName =
              authorUserMatch.info.firstName +
                ' ' +
                authorUserMatch.info.lastName || '';
            oneCombinedRes.authorEmailAddress =
              authorUserMatch.info.email || '';
          } else {
            oneCombinedRes.authorName = '';
            oneCombinedRes.authorEmailAddress = '';
          }
          combinedResults.push(oneCombinedRes);
        });
        setFilteredTasks(combinedResults);
        if (isAllTasks) {
          setAllTasks(combinedResults);
        }
        if (updateCounts) {
          loadTaskCounts(fromDate, toDate);
        }
        if (openTaskId) {
          let matchingTask = combinedResults.find(one => one.id === openTaskId);
          if (matchingTask) {
            setTaskBeingEdited(matchingTask);
            setIsEditTaskDialogOpen(true);
          }
        }
      });
    } else {
      setFilteredTasks([]);
      if (isAllTasks) {
        setAllTasks([]);
      }
      if (updateCounts) {
        loadTaskCounts(fromDate, toDate);
      }
    }
  };
  const closeEditTaskDialog = () => {
    setIsEditTaskDialogOpen(false);
    filtersChangedCallback(passedFilters, true);
  };
  const clickedTask = task => {
    setTaskBeingEdited(task);
    setIsEditTaskDialogOpen(true);
  };
  const editedTaskCallback = (task, assignedUserChanged) => {
    if (assignedUserChanged) {
      setIsEditTaskDialogOpen(false);
      setTaskBeingEdited(null);
      loadTasks();
      setIsLoading(false);
    } else {
      setTaskBeingEdited(task);
      setRefreshSeed(+new Date());
    }
  };
  const clearFilters = () => {
    filtersChangedCallback({ status: [], priority: [], dateFilters: {} });
  };
  const filtersChangedCallback = (filters, updateCounts) => {
    let newCurrentlyFiltered = false;
    let checkedDateOptions;
    let fromDate = null,
      toDate = null,
      fromTimestamp,
      toTimestamp;
    let queryString = '',
      oneQueryStringPart = '',
      queryStringParts = [],
      statusQueryStringPart = '',
      priorityQueryStringPart = '',
      dueDateQueryStringPart = '';
    if (filters.status.length) {
      queryStringParts = [];
      filters.status.forEach(one => {
        oneQueryStringPart = `{"field":"'status'","op":"=","value":"${one}","internOp":"OR"}`;
        queryStringParts.push(oneQueryStringPart);
      });
      statusQueryStringPart = '[' + queryStringParts.join(',') + ']';
      newCurrentlyFiltered = true;
    }
    if (filters.priority.length) {
      queryStringParts = [];
      filters.priority.forEach(one => {
        oneQueryStringPart = `{"field":"'priority'","op":"=","value":"${one}","internOp":"OR"}`;
        queryStringParts.push(oneQueryStringPart);
      });
      priorityQueryStringPart = '[' + queryStringParts.join(',') + ']';
      newCurrentlyFiltered = true;
    }
    fromDate = filters?.dateFilters?.fromDate || null;
    toDate = filters?.dateFilters?.toDate || null;

    if (filters.dateFilters && filters.dateFilters?.checkedOptions?.length) {
      queryStringParts = [];
      checkedDateOptions = filters.dateFilters.checkedOptions || [];
      let todayDate, endDate, d;
      d = new Date();
      d.setUTCHours(12);
      d.setMinutes(0);
      d.setSeconds(0);
      d.setMilliseconds(0);
      todayDate = d;

      if (checkedDateOptions.includes('today')) {
        queryStringParts.push(
          `{"field":"'dueDate'","op":"=","value":${+todayDate},"internOp":"OR"}`
        );
      }
      if (checkedDateOptions.includes('next_7_days')) {
        endDate = addDaysToDateObject(todayDate, 7);
        queryStringParts.push(
          `{"field":"'dueDate'","op":">=","value":${+todayDate},"internOp":"OR"},{"field":"'dueDate'","op":"<=","value":${+endDate}}`
        );
      }
      if (checkedDateOptions.includes('next_30_days')) {
        endDate = addDaysToDateObject(todayDate, 30);
        queryStringParts.push(
          `{"field":"'dueDate'","op":">=","value":${+todayDate},"internOp":"OR"},{"field":"'dueDate'","op":"<=","value":${+endDate}}`
        );
      }
      if (checkedDateOptions.includes('overdue')) {
        queryStringParts.push(
          `{"field":"'dueDate'","op":"<","value":${+todayDate},"internOp":"OR"}`
        );
      }

      if (checkedDateOptions.includes('custom_range')) {
        if (fromDate) {
          fromTimestamp = +new Date(fromDate);
          oneQueryStringPart = `{"field":"'dueDate'","op":">=","value":${fromTimestamp},"internOp":"OR"}`;
          queryStringParts.push(oneQueryStringPart);
        }
        if (toDate) {
          toTimestamp = +new Date(toDate);
          if (fromDate) {
            oneQueryStringPart = `{"field":"'dueDate'","op":"<=","value":${toTimestamp}}`;
          } else {
            oneQueryStringPart = `{"field":"'dueDate'","op":"<=","value":${toTimestamp},"internOp":"OR"}`;
          }
          queryStringParts.push(oneQueryStringPart);
        }
      }

      dueDateQueryStringPart = '[' + queryStringParts.join(',') + ']';
      newCurrentlyFiltered = true;
    }

    if (
      statusQueryStringPart ||
      priorityQueryStringPart ||
      dueDateQueryStringPart
    ) {
      queryStringParts = [];
      if (statusQueryStringPart) {
        queryStringParts.push(statusQueryStringPart);
      }
      if (priorityQueryStringPart) {
        queryStringParts.push(priorityQueryStringPart);
      }
      if (dueDateQueryStringPart) {
        queryStringParts.push(dueDateQueryStringPart);
      }
      queryString = '?query=[' + queryStringParts.join(',') + ']';
    }

    const rangeChanged =
      fromDate !== passedFilters?.dateFilters?.fromDate ||
      toDate !== passedFilters?.dateFilters?.toDate;

    fetchAPI(`/tasks/v1/eu/tasks${queryString}`).then(tasksRes => {
      updateTaskTableFromAPIResults(
        tasksRes,
        false,
        rangeChanged,
        fromDate,
        toDate
      );
      setCurrentlyFiltered(newCurrentlyFiltered);
      setPassedFilters({
        status: filters.status || [],
        priority: filters.priority || [],
        dateFilters: { ...filters.dateFilters } || {}
      });
    });
  };
  const getSortedRows = (rowData, colId, newSortValue) => {
    let newSortedRows = [...rowData];
    const descFactor = newSortValue === 'desc' ? -1 : 1;
    if (colId === 'formattedPriority') {
      newSortedRows.sort((a, b) => {
        const possibleValues = ['low', 'medium', 'high'];
        const valA = possibleValues.indexOf(a.priority);
        const valB = possibleValues.indexOf(b.priority);
        if (valA < valB) {
          return -descFactor;
        }
        if (valA > valB) {
          return descFactor;
        }
        return 0;
      });
    } else {
      const comparisonColumnId =
        colId === 'formattedDueDate'
          ? 'dueDate'
          : colId === 'authorDisplay'
          ? 'authorName'
          : colId === 'assigneeDisplay'
          ? 'assigneeName'
          : colId;
      newSortedRows.sort((a, b) => {
        const valA = ('' + a[comparisonColumnId]).toUpperCase();
        const valB = ('' + b[comparisonColumnId]).toUpperCase();
        if (valA < valB) {
          return -descFactor;
        }
        if (valA > valB) {
          return descFactor;
        }

        // names must be equal
        return 0;
      });
    }
    return newSortedRows;
  };

  const [sortedRows, setSortedRows] = useState(
    getSortedRows(filteredTasks, INITIAL_SORT.id, INITIAL_SORT.value)
  );

  const onSortChange = (colId, newSortValue) => {
    const newSortedRows = getSortedRows(filteredTasks, colId, newSortValue);
    setSortObject({ id: colId, value: newSortValue });
    setSortedRows(newSortedRows);
  };

  useEffect(() => {
    setSortedRows(
      getSortedRows(filteredTasks, sortObject.id, sortObject.value)
    );
  }, [filteredTasks]);

  useEffect(() => {
    const pagePath = window.location.pathname;
    const splitPath = pagePath.split('/');
    const taskId = splitPath[2] === 'tasks' ? splitPath[3] : null;
    loadCanReassign();
    loadTasks(taskId);
  }, []);
  const viewProps = {
    canReassign,
    clearFilters,
    clickedTask,
    closeEditTaskDialog,
    counts,
    currentlyFiltered,
    editedTaskCallback,
    filtersChangedCallback,
    isEditTaskDialogOpen,
    taskBeingEdited,
    refreshSeed,
    sortedRows,
    sortObject,
    onSortChange,
    passedFilters
  };

  return <EndUserTaskTableView {...viewProps} />;
};

export default EndUserTaskTableEngine;
